import React from 'react';
import { ColumnsType } from 'antd/es/table';
import { useAtom } from "jotai";
import { useHttpQuery } from '@esg/framework';
import { agencyApis } from '@esg/business-agency';
import { BranchItem } from '@esg/business-agency/api/branch';
import { dayjs } from '@esg/shared';
import { FlexBox, TextCommon, dateRangeState, usePagination, TableCustom } from '@esg/ui';
import { Tag, Empty, Spin, Pagination } from 'antd';

interface UsageTableContainerProps {
    readonly onSelectBranch: (branch: BranchItem) => void;
}

const PAGE_SIZE = 8;

const UsageTableContainer: React.FC<UsageTableContainerProps> = ({ onSelectBranch }) => {
    const [searchParams, setSearchParams] = usePagination({
        page: 1,
        pageSize: PAGE_SIZE,
        orderBy: 'id',
        orderDirection: 'asc'
    });
    const [dateRange] = useAtom(dateRangeState);

    const startTime = dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : dayjs().startOf('year').format('YYYY-MM-DD');
    const endTime = dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');

    const { data: branchesResponse, isFetching: isBranchesLoading } = useHttpQuery(
        agencyApis.branch.getBranches,
        {
            query: {
                ...searchParams,
                dateCacheKey: `${startTime}_${endTime}`
            }
        }
    );

    const lastDataRef = React.useRef<typeof branchesResponse | undefined>(undefined);
    if (branchesResponse?.items) {
        lastDataRef.current = branchesResponse;
    }

    const displayData = branchesResponse ?? lastDataRef.current;
    const branches = displayData?.items ?? [];

    const rowSlots = Array.from({ length: PAGE_SIZE }, (_, i) => branches[i]);

    // Only keep subscription queries needed for plan, price, status, and dates
    const subQueries = rowSlots.map(branch =>
        useHttpQuery(
            agencyApis.subscription.getAllBranchSubscription,
            { query: { ...searchParams, page: 1, branchId: branch?.id, startTime, endTime } },
            { enabled: !!branch }
        )
    );

    const isMetricsLoading = subQueries.some(q => q.isFetching);
    const isEverythingLoading = isBranchesLoading || (branches.length > 0 && isMetricsLoading);

    const handlePageChange = (newPage: number) => {
        setSearchParams({ ...searchParams, page: newPage });
        const scrollBody = document.querySelector('.usage-table-wrapper .ant-table-body');
        if (scrollBody) {
            scrollBody.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            const wrapper = document.querySelector('.usage-table-wrapper');
            wrapper?.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const cellStyle = { padding: '2vh', height: '9vh' };

    const cellBorderProps = () => ({
        style: { borderRight: '1px solid #f0f0f0', ...cellStyle }
    });

    const headerBorderProps = () => ({
        style: { borderRight: '1px solid #f0f0f0' }
    });

    const tableColumns: ColumnsType<BranchItem> = [
        {
            title: 'Salon Name',
            key: 'salonName',
            width: 140,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            sorter: (a, b) => a.organizationName && b.organizationName ? a.organizationName.localeCompare(b.organizationName) : 0,
            render: (_, record) => <TextCommon fontSize={13} color="#262626" fontWeight={500}>{record.organizationName || '-'}</TextCommon>
        },
        {
            title: 'Branch Name',
            key: 'branchName',
            width: 140,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            sorter: (a, b) => a.name && b.name ? a.name.localeCompare(b.name) : 0,
            render: (_, record) => (
                <TextCommon onClick={() => onSelectBranch(record)} color="#1890ff" fontWeight={600} cursor="pointer">
                    {record.name || 'Unknown'}
                </TextCommon>
            )
        },
        {
            title: 'Plan Name',
            key: 'planName',
            width: 150,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, __, index) => {
                const latestSub = subQueries[index]?.data?.items?.[0];
                return <TextCommon fontSize={13} fontWeight={500} color="#262626">{latestSub ? latestSub.name : "No Active Plan"}</TextCommon>;
            }
        },
        {
            title: 'Price',
            key: 'price',
            width: 100,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, __, index) => {
                const latestSub = subQueries[index]?.data?.items?.[0];
                return <TextCommon fontSize={13} fontWeight={500}>${latestSub ? latestSub.price : '0'}</TextCommon>;
            }
        },
        {
            title: 'Status',
            key: 'status',
            width: 110,
            align: 'center',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, __, index) => {
                const latestSub = subQueries[index]?.data?.items?.[0];
                const isActive = latestSub?.planSubscription?.isActive === true;
                if (!latestSub || !isActive)
                    return <Tag color="error" style={{ minWidth: 56, textAlign: 'center', margin: 0 }}>Expired</Tag>;
                return <Tag color="success" style={{ minWidth: 56, textAlign: 'center', margin: 0 }}>Active</Tag>;
            }
        },
        {
            title: 'Effective Date',
            key: 'effectiveDate',
            width: 130,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, __, index) => {
                const latestSub = subQueries[index]?.data?.items?.[0];
                return <TextCommon fontSize={12} color="#8c8c8c">{latestSub && latestSub.effectiveTime ? dayjs(latestSub.effectiveTime).format('MM/DD/YYYY') : '-'}</TextCommon>;
            }
        },
        {
            title: 'Expiration Date',
            key: 'expirationDate',
            width: 130,
            align: 'left',
            onCell: cellBorderProps,
            onHeaderCell: headerBorderProps,
            render: (_, __, index) => {
                const latestSub = subQueries[index]?.data?.items?.[0];
                return <TextCommon fontSize={12} color="#8c8c8c">{latestSub && latestSub.expiredTime ? dayjs(latestSub.expiredTime).format('MM/DD/YYYY') : '-'}</TextCommon>;
            }
        }
    ];

    return (
        <FlexBox direction="column" className="table-section" width="100%" height="70vh">
            <FlexBox
                className="usage-table-wrapper"
                direction="column"
                width="max(350px, calc(100vw - 280px))"
                height="70vh"
                backgroundColor="white"
                borderRadius={0}
            >
                {isEverythingLoading ? (
                    <FlexBox direction="column" preset="column-center" height={100}>
                        <Spin size="large" />
                    </FlexBox>
                ) : (
                    <TableCustom
                        className="usage-table"
                        columns={tableColumns}
                        dataSource={branches}
                        pagination={false}
                        bordered
                        sticky
                        showHeader={false}
                        footer={() => (
                            <Pagination
                                className="custom-pagination"
                                current={displayData?.meta?.page ?? 1}
                                total={displayData?.meta?.totalItems ?? 0}
                                pageSize={displayData?.meta?.pageSize ?? PAGE_SIZE}
                                showSizeChanger={false}
                                onChange={handlePageChange}
                            />
                        )}
                        rowClassName={() => 'usage-table-row'}
                        locale={{
                            emptyText: (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description="No branches found."
                                />
                            )
                        }}
                    />
                )}
            </FlexBox>
        </FlexBox>
    );
};

export { UsageTableContainer };